import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const MockDateButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;

  &:hover {
    background: #ff5252;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  min-width: 320px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 18px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  margin-bottom: 20px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  margin-bottom: 20px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-size: 14px;
  font-weight: 600;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ variant }) =>
    variant === 'primary'
      ? `
    background: #007bff;
    color: white;

    &:hover {
      background: #0056b3;
    }
  `
      : `
    background: #f8f9fa;
    color: #333;
    border: 1px solid #dee2e6;

    &:hover {
      background: #e9ecef;
    }
  `}
`;

const ClearButton = styled.button`
  padding: 8px 16px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #c82333;
  }
`;

const DevTools: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [optimizelyUserId, setOptimizelyUserId] = useState('');

  useEffect(() => {
    // Load existing values from session storage
    const savedDate = sessionStorage.getItem('mock-date');
    if (savedDate) {
      setSelectedDate(savedDate);
    }
    const savedOptimizelyUserId = localStorage.getItem('optimizelyUserId');
    if (savedOptimizelyUserId) {
      setOptimizelyUserId(savedOptimizelyUserId);
    }
  }, []);

  const handleSave = () => {
    if (selectedDate) {
      sessionStorage.setItem('mock-date', selectedDate);
    } else {
      sessionStorage.removeItem('mock-date');
    }
    if (optimizelyUserId) {
      localStorage.setItem('optimizelyUserId', optimizelyUserId);
    } else {
      localStorage.removeItem('optimizelyUserId');
    }
    setIsModalOpen(false);
    // Refresh the page to apply the new mock date
    window.location.reload();
  };

  const handleClear = () => {
    sessionStorage.removeItem('mock-date');
    localStorage.removeItem('optimizelyUserId');
    setSelectedDate('');
    setOptimizelyUserId('');
    setIsModalOpen(false);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    // Reset to saved values if modal is closed without saving
    const savedDate = sessionStorage.getItem('mock-date');
    const savedOptimizelyUserId = localStorage.getItem('optimizelyUserId');
    setSelectedDate(savedDate || '');
    setOptimizelyUserId(savedOptimizelyUserId || '');
  };

  // Only show in non-production environments
  if (process.env.GATSBY_ENV === 'production') {
    return null;
  }

  return (
    <>
      <MockDateButton onClick={() => setIsModalOpen(true)}>Mock Settings</MockDateButton>
      {isModalOpen && (
        <ModalOverlay onClick={handleClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Mock Settings</ModalTitle>
              <CloseButton onClick={handleClose}>&times;</CloseButton>
            </ModalHeader>

            <Label htmlFor="mock-date">Mock Date</Label>
            <DateInput
              id="mock-date"
              type="datetime-local"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              placeholder="Select date and time"
            />

            <Label htmlFor="optimizely-user-id">Optimizely User ID</Label>
            <TextInput
              id="optimizely-user-id"
              type="text"
              value={optimizelyUserId}
              onChange={(e) => setOptimizelyUserId(e.target.value)}
              placeholder="Enter Optimizely User ID"
            />

            <ButtonGroup>
              <ClearButton onClick={handleClear}>Clear</ClearButton>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave}>
                Save
              </Button>
            </ButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default DevTools;
